import axios, { AxiosError, AxiosInstance } from 'axios';
import Cookie from 'js-cookie'; // Imported to clear cookies on failure

import { API_ENDPOINT } from '../config';

const request: AxiosInstance = axios.create({ baseURL: API_ENDPOINT });

/**
 * Pages that an anonymous visitor is *supposed* to reach.
 *
 * The app mounts providers at the root that fetch app config on every page —
 * those calls 401 for a signed-out visitor, and bouncing to /login on that 401
 * kicked people straight back off the setup and reset-password pages, which is
 * exactly where they were sent to go. Being signed out is the normal state on
 * these routes, so a 401 here is information, not a session expiry.
 */
const PUBLIC_PATHS = ['/login', '/setup-account', '/reset-password', '/set-password'];

const isPublicPath = (pathname: string) =>
    PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Is this 401 just "nobody is signed in", on the page that decides where an
 * anonymous visitor goes?
 *
 * `/` is that page: the router sends a signed-out visitor to /login or, while
 * accounts are still being set up, to /setup-account. Hard-redirecting to /login
 * from here on a background 401 would always win that race and take the choice
 * away. Only when the `user` cookie still holds a value — meaning we thought we
 * had a live session and it has expired underneath us — is the redirect the
 * right answer, and then it happens as before.
 */
const isAnonymousLanding = (pathname: string) => pathname === '/' && !Cookie.get('user');

// Redirect to login on 401 (session expired)
request.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        const url = error.config?.url || '';
        // Don't redirect on session-check — it's expected to return 401 for unauthenticated users
        const isSessionCheck = url.includes('/auth/session-check');
        // Added 'typeof window !== 'undefined'' to prevent crashes on Node.js/SSR side
        if (
            typeof window !== 'undefined' &&
            error.response?.status === 401 &&
            !isPublicPath(window.location.pathname) &&
            !isAnonymousLanding(window.location.pathname) &&
            !isSessionCheck
        ) {
            Cookie.remove('user');
            Cookie.remove('td_token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);
class Request {
    request: AxiosInstance;

    constructor() {
        this.request = request;
    }
}

export default request;

export { Request };
